"use client";

import * as RadixSelect from "@radix-ui/react-select";
import { Check, ChevronDown } from "lucide-react";
import { cn } from "./cn";
import { Field } from "./Field";

export interface SelectOption {
  value: string;
  label: string;
  /** Optional secondary line shown under the label inside the menu only. */
  description?: string;
  disabled?: boolean;
}

export interface SelectProps {
  label: string;
  helper?: string;
  error?: string;
  required?: boolean;
  placeholder?: string;
  options: SelectOption[];
  value: string | undefined;
  onValueChange: (value: string) => void;
  disabled?: boolean;
  compact?: boolean;
  name?: string;
  /** Stable id for the trigger (label `htmlFor` target). Auto-generated when omitted. */
  id?: string;
}

/**
 * Radix Select forbids `""` as an item value (it is reserved for "no
 * selection"). Filters use `""` for "Todos", so we map it to a private
 * sentinel on the way in and back to `""` on the way out — callers keep
 * their existing `""` semantics.
 */
const EMPTY_SENTINEL = "__asv_empty__";
const toRadix = (value: string) => (value === "" ? EMPTY_SENTINEL : value);
const fromRadix = (value: string) => (value === EMPTY_SENTINEL ? "" : value);

/**
 * Single choice from a short, local list (até ~10 opções). Para listas
 * longas ou dinâmicas (clientes, pessoas) use Combobox. Nenhum `<select>`
 * nativo é o padrão final — ver apps/portal/docs/padroes-componentes.md.
 */
export function Select({
  label,
  helper,
  error,
  required,
  placeholder = "Selecione",
  options,
  value,
  onValueChange,
  disabled,
  compact,
  name,
  id,
}: SelectProps) {
  const hasValue = value !== undefined && options.some((option) => option.value === value);
  return (
    <Field label={label} helper={helper} error={error} required={required} htmlFor={id}>
      {({ fieldId, describedBy }) => (
        <RadixSelect.Root
          // "" = controlled "nothing selected" → Radix shows the placeholder.
          value={hasValue ? toRadix(value) : ""}
          onValueChange={(next) => onValueChange(fromRadix(next))}
          disabled={disabled}
          name={name}
          required={required}
        >
          <RadixSelect.Trigger
            id={fieldId}
            className={cn("ui-select-trigger", compact && "compact")}
            aria-invalid={Boolean(error) || undefined}
            aria-describedby={describedBy}
          >
            <span className="ui-select-value">
              <RadixSelect.Value placeholder={placeholder} />
            </span>
            <RadixSelect.Icon className="ui-select-icon">
              <ChevronDown size={16} aria-hidden="true" />
            </RadixSelect.Icon>
          </RadixSelect.Trigger>
          <RadixSelect.Portal>
            <RadixSelect.Content className="ui-select-content" position="popper" sideOffset={6}>
              <RadixSelect.Viewport className="ui-select-viewport">
                {options.map((option) => (
                  <RadixSelect.Item
                    key={option.value}
                    value={toRadix(option.value)}
                    disabled={option.disabled}
                    className="ui-select-item"
                  >
                    <span className="ui-select-item-text">
                      <RadixSelect.ItemText>{option.label}</RadixSelect.ItemText>
                      {option.description ? (
                        <span className="ui-select-item-description">{option.description}</span>
                      ) : null}
                    </span>
                    <RadixSelect.ItemIndicator className="ui-select-item-indicator">
                      <Check size={15} aria-hidden="true" />
                    </RadixSelect.ItemIndicator>
                  </RadixSelect.Item>
                ))}
              </RadixSelect.Viewport>
            </RadixSelect.Content>
          </RadixSelect.Portal>
        </RadixSelect.Root>
      )}
    </Field>
  );
}
